import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { PencilSquareIcon } from '@heroicons/react/20/solid';
import { useSetTitle } from '../../hooks';
import { withAuth } from '../../hocs';
import { Button } from '../../components/UI';
import { InspoJourney, InspoStory, useCapabilities, useInspo, useLogin } from '../../services';
import { isSuperAccess } from '../../utils';
import { StoryHero } from './components/StoryHero';
import { StoryPicker } from './components/StoryCards';
import { Benchmark, FoundersStory } from './components/Playbook';
import { NextStep } from './components/NextStep';
import { InspoAdminSlideover } from './components/InspoAdminSlideover';

const JOURNEY_KEY = 'inspo:journey';
const JOURNEYS: { id: InspoJourney; label: string }[] = [
    { id: 'store', label: 'I run a store' },
    { id: 'artist', label: "I'm an artist" }
];

// #region readJourney
const readJourney = (): InspoJourney | null => {
    try {
        const v = window.localStorage.getItem(JOURNEY_KEY);
        return v === 'store' || v === 'artist' ? v : null;
    } catch {
        return null;
    }
};

// #region JourneyToggle
const JourneyToggle: React.FunctionComponent<{ value: InspoJourney; onChange: (j: InspoJourney) => void }> = ({ value, onChange }) => (
    <div role="radiogroup" aria-label="Show stories for" className="inline-flex rounded-xl border border-gray-200 bg-surface-alt p-1 dark:border-stone-700">
        {JOURNEYS.map((j) => (
            <button key={j.id} type="button" role="radio" aria-checked={value === j.id} onClick={() => onChange(j.id)}
                className={clsx('rounded-lg px-3 py-1.5 text-sm font-medium transition',
                    value === j.id ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white')}>
                {j.label}
            </button>
        ))}
    </div>
);

// #region Skeleton
const Skeleton = () => (
    <div className="space-y-6" aria-busy="true">
        <div className="h-96 animate-pulse rounded-2xl bg-gray-100 dark:bg-stone-800" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => <div key={i} className="h-72 animate-pulse rounded-2xl bg-gray-100 dark:bg-stone-800" />)}
        </div>
    </div>
);

// #region InspoContent
/**
 * Real brands growing on Wallmates, as case studies, ending in one clear next step. Shared by the
 * dashboard tab and the public stories page (publicMode: no login, no admin, buttons open sign-up).
 */
export const InspoContent: React.FunctionComponent<{ publicMode?: boolean; initialJourney?: InspoJourney }> = ({ publicMode = false, initialJourney }): JSX.Element => {
    const [user] = useLogin();
    const superAccess = !publicMode && isSuperAccess(user);
    const { payload, loading, failed, load } = useInspo(publicMode);
    const { hasShopify, resolved } = useCapabilities();
    const [picked, setPicked] = useState<InspoJourney | null>(() => initialJourney ?? readJourney());
    const [adminOpen, setAdminOpen] = useState(false);
    const [pickedKey, setPickedKey] = useState<string | null>(null);
    const journey: InspoJourney = picked ?? (publicMode || (resolved && !hasShopify) ? 'artist' : 'store');

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, []);

    const choose = (j: InspoJourney) => {
        setPicked(j);
        try { window.localStorage.setItem(JOURNEY_KEY, j); } catch { /* private mode: keep it for this visit */ }
    };

    // The hero is the story the viewer picked, else the first featured founder on their journey.
    const featured = useMemo<InspoStory[]>(() => payload?.featured ?? [], [payload]);
    const active = useMemo<InspoStory | null>(() => featured.find((s) => s.key === pickedKey)
        ?? featured.find((s) => s.journey === journey)
        ?? featured[0]
        ?? payload?.hero
        ?? null, [featured, pickedKey, journey, payload]);

    return (
        <div className="mx-auto flex max-w-7xl flex-col gap-8 pb-10">
            <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div className="space-y-1">
                    <p className="text-xs font-semibold uppercase tracking-wider text-brand">Inspo</p>
                    <h1 className="font-display text-4xl font-semibold leading-tight tracking-tight text-black sm:text-5xl dark:text-white">Real people, real wallpaper businesses</h1>
                    <p className="max-w-2xl text-sm text-gray-600 dark:text-gray-300">
                        How artists and brands on Wallmates built what they have, in their numbers. Then your next step.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <JourneyToggle value={journey} onChange={(j) => { choose(j); setPickedKey(null); }} />
                    {superAccess && <Button outline onClick={() => setAdminOpen(true)}><PencilSquareIcon data-slot="icon" />Edit stories</Button>}
                </div>
            </header>

            {loading && !payload && <Skeleton />}
            {failed && !payload && (
                <div className="rounded-2xl border border-gray-200 p-8 text-center text-sm text-gray-600 dark:border-stone-700 dark:text-gray-300">
                    Stories didn&apos;t load. <button type="button" className="font-medium text-blue-600 underline dark:text-blue-400" onClick={() => load(true)}>Try again</button>
                </div>
            )}

            {active && <StoryHero story={active} />}

            <StoryPicker stories={featured} activeKey={active?.key ?? null} onPick={setPickedKey} />

            {payload && <NextStep journey={journey} />}

            {payload && <Benchmark playbook={payload.playbook} rising={payload.rising} />}

            {payload?.house && <FoundersStory house={payload.house} />}

            {payload && (
                <p className="text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                    Figures are real customer sales fulfilled by Wallmates, rounded to the nearest $1k or shown as ranges. Brands shown by name agreed to be featured; the others are anonymous.
                    Shaded months were during the Wallmates beta, when not every order ran through us yet, so those months don&apos;t show the brands&apos; full sales.
                    Every business is different, and Wallmates doesn&apos;t promise any level of sales or earnings.
                </p>
            )}

            {superAccess && <InspoAdminSlideover open={adminOpen} setOpen={setAdminOpen} onSaved={() => load(true)} />}
        </div>
    );
};

// #region Inspo
/** The dashboard tab. */
const Inspo: React.FunctionComponent = (): JSX.Element => {
    const setTitle = useSetTitle();
    useEffect(() => { setTitle('Inspo'); }, [setTitle]);
    return <InspoContent />;
};

/** The page without the auth wrapper, for previews. */
export const InspoPage = Inspo;

export default withAuth(Inspo as any, 'USER');
